import type { AuthProvider } from "react-admin";
import { queryClient } from "./queryClient";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000";

const TOKEN_KEY = "aere_access_token";
const USER_KEY = "aere_user";

// Must match the query key react-admin's usePermissions() hook uses
// internally (ra-core/dist/auth/usePermissions.js: ['auth', 'getPermissions', params],
// called here with no params so params defaults to {}). Seeding this
// synchronously on login/refresh avoids the async invalidate-then-refetch
// race that otherwise leaves admin-gated UI (Users tab, Create Project)
// hidden until a hard refresh.
const PERMISSIONS_QUERY_KEY = ["auth", "getPermissions", {}];

interface LoginResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    role: string;
  };
}

interface RefreshResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    role: string;
  };
}

interface StoredUser {
  id: string;
  email: string;
  role: string;
}

function parseJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64 = token.split(".")[1];
    const decoded = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return {};
  }
}

// Module-level shared in-flight refresh promise.
// All concurrent callers (checkAuth, authHttpClient) share the same promise so
// only a single POST /auth/refresh is ever in flight at a time.
let _refreshPromise: Promise<string> | null = null;

/**
 * Attempt to mint a fresh access token from the shared httpOnly refresh cookie.
 * Returns the new access token on success, throws on failure.
 * Deduplicates concurrent callers — only one fetch is made at a time.
 */
export async function refreshAccessToken(): Promise<string> {
  if (_refreshPromise) {
    return _refreshPromise;
  }
  _refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        queryClient.setQueryData(PERMISSIONS_QUERY_KEY, null);
        throw new Error("Session expired");
      }
      const data = (await response.json()) as RefreshResponse;
      localStorage.setItem(TOKEN_KEY, data.accessToken);
      if (data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        queryClient.setQueryData(PERMISSIONS_QUERY_KEY, data.user.role);
      }
      return data.accessToken;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

const authProvider: AuthProvider = {
  async login({ username, password }: { username: string; password: string }) {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: username, password }),
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        message?: string;
      };
      throw new Error(body.message ?? "Invalid credentials");
    }

    const data = (await response.json()) as LoginResponse;
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    queryClient.setQueryData(PERMISSIONS_QUERY_KEY, data.user.role);
  },

  async logout() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      }).catch(() => {
        // ignore network errors on logout
      });
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Clear the cached permissions so a subsequent login in this tab (e.g. a
    // different user) can't briefly render with the previous session's role.
    queryClient.setQueryData(PERMISSIONS_QUERY_KEY, null);
    // Only notify the viewer if this logout was user-initiated, not if it was
    // already triggered by an incoming aere:logout message from the viewer.
    // Sending back when triggered by a message would create a ping-pong loop.
    const fromMessage = (window as Window & { _loggingOutFromMessage?: boolean })._loggingOutFromMessage;
    if (!fromMessage && window.opener && !window.opener.closed) {
      window.opener.postMessage(
        { type: 'aere:logout' },
        import.meta.env.VITE_VIEWER_URL ?? 'http://localhost:3000'
      );
    }
    // Always clear the flag after logout completes.
    (window as Window & { _loggingOutFromMessage?: boolean })._loggingOutFromMessage = false;

    // Only redirect to the viewer when an actual session was torn down. react-admin
    // also calls logout() automatically when checkAuth/checkError fail (e.g. an
    // unauthenticated visit, or an expired/cleared token), and in those cases there
    // is no token here — we must NOT bounce the visitor to the viewer, but instead
    // let react-admin fall through to its default redirect to the /login screen.
    if (token) {
      // Navigate back to the 3D viewer home page (a separate origin), rather than
      // staying on the admin login screen. Returning false suppresses
      // react-admin's default in-app redirect to /login.
      window.location.href = import.meta.env.VITE_VIEWER_URL ?? "http://localhost:3000";
      return false;
    }
  },

  async checkAuth() {
    const token = localStorage.getItem(TOKEN_KEY);

    // No token at all — attempt SSO bootstrap from the shared refresh cookie.
    if (!token) {
      await refreshAccessToken();
      return;
    }

    // Token exists — check whether it is expired.
    const payload = parseJwtPayload(token);
    const exp = payload["exp"] as number | undefined;
    if (exp !== undefined && Date.now() / 1000 > exp) {
      // Expired — try to silently renew via the cookie.
      await refreshAccessToken();
    }
    // Token is valid and not expired — nothing to do.
  },

  async checkError({ status }: { status: number }) {
    if (status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      queryClient.setQueryData(PERMISSIONS_QUERY_KEY, null);
      throw new Error("Unauthorized");
    }
    // 403 means authenticated but not permitted — don't log out
    if (status === 403) {
      throw new Error("Forbidden");
    }
  },

  async getPermissions() {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;
    const user = JSON.parse(userStr) as StoredUser;
    return user.role;
  },

  async getIdentity() {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) throw new Error("Not authenticated");
    const user = JSON.parse(userStr) as StoredUser;
    return {
      id: user.id,
      fullName: user.email,
      avatar: undefined,
    };
  },
};

export { TOKEN_KEY, USER_KEY };
export default authProvider;
